import { App } from './App';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';

export type NotificationData = {
  id: string;
  type: 'error' | 'success';
  message: string;
  timeout?: number; // 0 means no timeout
  timeoutRef?: NodeJS.Timeout;
  path?: string;
};

export type ValidationFieldError = {
  property?: string;
  messageTemplate: string;
};

export type ErrorResponse = {
  property?: string;
  message: string;
  messageTemplate?: string;
  name: 'ValidationError';
  fieldErrors?: ValidationFieldError[];
  response: {
    data: {
      errors?: ValidationFieldError[];
    };
  };
  errors?: ValidationFieldError[];
  status?: number;
  timeout?: number;
};

export type AppError = {
  name: string;
  errorCode?: number;
  errorResponse?: string | ErrorResponse;
};

export type ErrorManagerContextType = {
  error?: string;
  errorCode?: number;
  errorResponse?: string | ErrorResponse;
  setError: (errorResponse?: string | ErrorResponse) => void;
  errors: Array<AppError>;
  addErrors: (errors: Array<AppError>) => Array<string>;
  addError: <TError extends AppError>(error: TError) => string;
  removeError: (key: string) => void;
  removeErrors: (keys: Array<string>) => void;
  addNotification: (
    notification: Omit<NotificationData, 'id'>,
  ) => NotificationData;
  removeNotification: (id: string) => void;
  notificationsData: Array<NotificationData>;
  errorToNotifications: (
    errors: ErrorResponse,
    path?: string,
  ) => Array<Omit<NotificationData, 'id'>>;
};

export const ErrorManagerContext = createContext<ErrorManagerContextType>(
  {} as ErrorManagerContextType,
);

export const useErrorManager = (): ErrorManagerContextType =>
  useContext(ErrorManagerContext);

export type ErrorManagerProviderProps = {
  app: App<any, any>;
  children: ReactNode;
};

export const ErrorManagerProvider = ({
  children,
  app,
}: ErrorManagerProviderProps): JSX.Element => {
  const [errorInfoResponse, setErrorInfoResponse] = useState<
    string | ErrorResponse
  >();
  const [errors, setErrors] = useState<Array<AppError & { key: string }>>([]);

  const setError = useCallback<ErrorManagerContextType['setError']>(
    (errorResponse) => {
      setErrorInfoResponse(errorResponse);
    },
    [],
  );

  const addError = useCallback<ErrorManagerContextType['addError']>((error) => {
    const key = `${error.name}-${uuid()}`;
    setErrors((errors) => [...errors, { ...error, key }]);
    return key;
  }, []);

  const addErrors = useCallback<ErrorManagerContextType['addErrors']>(
    (errors) => {
      const errorsWithKeys = errors.map((error) => ({
        ...error,
        key: `${error.name}-${uuid()}`,
      }));
      setErrors((errors) => [...errors, ...errorsWithKeys]);
      return errorsWithKeys.map((error) => error.key);
    },
    [],
  );

  const removeError = useCallback<ErrorManagerContextType['removeError']>(
    (key) => {
      setErrors((errors) => errors.filter((e) => e.key !== key));
    },
    [],
  );

  const removeErrors = useCallback<ErrorManagerContextType['removeErrors']>(
    (keys) => {
      setErrors((errors) =>
        errors.filter((e) => keys.includes(e.key) === false),
      );
    },
    [],
  );

  const [notificationsData, setNotificationsData] = useState<
    NotificationData[]
  >([]);

  const removeNotification = useCallback(
    (id: string) => {
      const notification = notificationsData.find((n) => n.id === id);

      if (notification?.timeoutRef) {
        clearTimeout(notification.timeoutRef);
      }

      setNotificationsData((notifications) =>
        notifications.filter((n) => n.id !== id),
      );
    },
    [notificationsData],
  );

  const addNotification = useCallback(
    (notification: Omit<NotificationData, 'id'>) => {
      const newNotification = { id: uuid(), timeout: 3500, ...notification };

      if (notification.message === '') {
        newNotification.message = app.translateError(app, 'default');
      }

      if (newNotification.timeout !== 0) {
        newNotification.timeoutRef = setTimeout(() => {
          removeNotification(newNotification.id);
        }, newNotification.timeout);
      }

      setNotificationsData((note) => [newNotification, ...note]);

      return newNotification;
    },
    [app, removeNotification],
  );

  const errorToNotifications = useCallback(
    (errors: ErrorResponse, path?: string): Omit<NotificationData, 'id'>[] => {
      const { name, fieldErrors } = errors;

      if (fieldErrors) {
        return fieldErrors.map((err: ValidationFieldError) => {
          const desc = app.translateError(
            app,
            path ? `.${path}` : err.messageTemplate,
          );
          return {
            type: 'error',
            message: desc,
          };
        });
      } else if (name) {
        const translationKey = path ? `.${path}` : name;
        const desc = app.translateError(app, translationKey);
        return [
          {
            type: 'error',
            message: desc,
          },
        ];
      } else {
        return [
          {
            type: 'error',
            message: 'default',
          },
        ];
      }
    },
    [app],
  );

  const contextValue = useMemo<ErrorManagerContextType>(
    () => ({
      errorResponse: errorInfoResponse,
      setError,
      addError,
      addErrors,
      errors,
      removeError,
      removeErrors,
      addNotification,
      removeNotification,
      notificationsData,
      errorToNotifications,
    }),
    [
      addError,
      addErrors,
      errors,
      errorInfoResponse,
      removeError,
      removeErrors,
      setError,
      addNotification,
      removeNotification,
      notificationsData,
      errorToNotifications,
    ],
  );

  return (
    <ErrorManagerContext.Provider value={contextValue}>
      {children}
    </ErrorManagerContext.Provider>
  );
};
